/**
 * Configuraciones centralizadas de animaciones y transiciones.
 * Optimizadas para rendimiento usando GPU acceleration.
 * Todas las animaciones usan transform y opacity para mejor rendimiento.
 */

/**
 * Easing functions optimizadas para diferentes tipos de animaciones
 */
export const easings = {
  // Easing suave para transiciones generales
  easeInOut: [0.4, 0, 0.2, 1],
  // Easing para entradas (ease-out)
  easeOut: [0, 0, 0.2, 1],
  // Easing para salidas (ease-in)
  easeIn: [0.4, 0, 1, 1],
  // Easing para efectos de rebote suave
  easeOutBack: [0.34, 1.56, 0.64, 1],
  // Easing para efectos elásticos sutiles
  easeOutElastic: [0.68, -0.55, 0.265, 1.55],
};

/**
 * Duraciones estándar de animaciones (en segundos)
 */
export const durations = {
  fast: 0.15, // Animaciones rápidas (hover, focus)
  normal: 0.3, // Animaciones normales (transiciones de página)
  slow: 0.5, // Animaciones lentas (modales, drawers)
  verySlow: 0.8, // Animaciones muy lentas (splash screen)
};

/**
 * Variantes de animación para fade (desvanecimiento)
 */
export const fadeVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: durations.normal,
      ease: easings.easeInOut,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: durations.fast,
      ease: easings.easeIn,
    },
  },
};

/**
 * Variantes de animación para slide (deslizamiento)
 */
export const slideVariants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: durations.normal,
      ease: easings.easeOut,
    },
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: {
      duration: durations.fast,
      ease: easings.easeIn,
    },
  },
};

/**
 * Variantes de animación para scale (escalado)
 */
export const scaleVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: durations.normal,
      ease: easings.easeOutBack,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: durations.fast,
      ease: easings.easeIn,
    },
  },
};

/**
 * Variantes de animación para slide up (deslizamiento hacia arriba)
 */
export const slideUpVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.normal,
      ease: easings.easeOut,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: durations.fast,
      ease: easings.easeIn,
    },
  },
};

/**
 * Variantes de animación para slide down (deslizamiento hacia abajo)
 */
export const slideDownVariants = {
  hidden: {
    opacity: 0,
    y: -20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.normal,
      ease: easings.easeOut,
    },
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: {
      duration: durations.fast,
      ease: easings.easeIn,
    },
  },
};

/**
 * Variantes de animación para hover en cards
 */
export const cardHoverVariants = {
  rest: {
    scale: 1,
    y: 0,
    transition: {
      duration: durations.fast,
      ease: easings.easeInOut,
    },
  },
  hover: {
    scale: 1.02,
    y: -4,
    transition: {
      duration: durations.fast,
      ease: easings.easeOut,
    },
  },
};

/**
 * Variantes de animación para entrada escalonada (stagger)
 */
export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

/**
 * Variantes de animación para elementos en lista
 */
export const staggerItem = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.normal,
      ease: easings.easeOut,
    },
  },
};

/**
 * Variantes de animación para modales
 */
export const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: durations.slow,
      ease: easings.easeOutBack,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: durations.fast,
      ease: easings.easeIn,
    },
  },
};

/**
 * Variantes de animación para backdrop (fondo de modales)
 */
export const backdropVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: durations.normal,
      ease: easings.easeInOut,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: durations.fast,
      ease: easings.easeIn,
    },
  },
};

/**
 * Variantes de animación para splash screen
 */
export const splashVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: durations.verySlow,
      ease: easings.easeOut,
    },
  },
  exit: {
    opacity: 0,
    scale: 1.1,
    transition: {
      duration: durations.normal,
      ease: easings.easeIn,
    },
  },
};

/**
 * Configuración de transición optimizada para GPU
 * Usa will-change para optimizar rendimiento
 */
export const getTransitionConfig = (duration = durations.normal, easing = easings.easeInOut) => ({
  duration,
  ease: easing,
  // Optimización para GPU
  willChange: "transform, opacity",
});

/**
 * Obtiene variantes de animación según el tipo
 * @param {string} type - Tipo de animación ('fade', 'slide', 'scale', 'slideUp', 'slideDown')
 * @returns {object} Variantes de animación
 */
export const getAnimationVariants = (type = "fade") => {
  const variantsMap = {
    fade: fadeVariants,
    slide: slideVariants,
    scale: scaleVariants,
    slideUp: slideUpVariants,
    slideDown: slideDownVariants,
    modal: modalVariants,
    splash: splashVariants,
  };

  return variantsMap[type] || fadeVariants;
};

